import React, { MouseEventHandler, useCallback, useRef, useState } from 'react';

import styled from 'styled-components';

import ArticleIcon from '@mui/icons-material/Article';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import {
  CoreDocumentTypeItem,
  DocumentFields,
  DocumentsSections,
} from '@cd3p/core/modules/documents';
import { APIDocument } from '@cd3p/core/types/api';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import {
  AddButton,
  ConfirmationDialog,
  IconButton,
  Menu,
  Skeleton,
  Stack,
  Typography,
} from 'components';
import {
  CursorProps,
  MoveHandler,
  NodeApi,
  NodeRendererProps,
  TreeApi,
} from 'react-arborist';
import { RowRendererProps } from 'react-arborist/src/types/renderers';
import { TreeProps } from 'react-arborist/src/types/tree-props';
import { useResizeDetector } from 'react-resize-detector';

import { ReactArboristTree, _, useTranslation } from 'third-party';

import { DocsSectionsTreeModal } from 'features/Docs/DocsSectionsTreeModal';

import { palette } from 'styles/theme';

const getNodeRowId = (nodeId: string) => `node-row-${nodeId}`;

const getTreeNodeFontSize = (level: number) => {
  switch (true) {
    case level >= 2:
      return '1.4';
    default:
      return '1.6';
  }
};

const getTreeNodeFontColor = (level: number, isCategory: boolean) => {
  switch (true) {
    case level === 0 && isCategory:
      return palette.primary900;
    default:
      return palette.darkText;
  }
};

const getTreeNodeFontWeight = (level: number, isCategory: boolean) => {
  switch (true) {
    case level === 0 && isCategory:
      return 700;
    default:
      return 600;
  }
};

const Wrapper = styled(Stack)`
  flex-grow: 1;
  align-items: stretch;
  min-height: 0;
`;

const TreeWrapper = styled.div`
  flex-grow: 1;
  min-height: 0;
`;

const EmptyState = styled(Stack)`
  padding: 2rem;
  align-items: center;
  flex-grow: 1;
`;

const EmptyStateText = styled(Typography)`
  font-weight: 400;
  font-size: 1.8rem;
  line-height: 150%;
  color: ${props => props.theme.custom.palette.gray};
`;

const MenuIconButton = styled(IconButton)`
  * {
    color: ${props => props.theme.custom.palette.darkText};
  }
`;

const TreeNodeWrapper = styled.div`
  flex-grow: 1;
  min-width: 0;
`;

const TreeCategoryArrowIcon = styled.span`
  cursor: pointer;
`;

const StyledAddButton = styled(AddButton)`
  justify-content: flex-start;
`;

const TreeCursor = styled.div`
  width: 100%;
  height: 0;
  border-bottom: 2px dotted ${props => props.theme.custom.palette.muted200};
  position: absolute;
`;

const TreeRowWrapper = styled(Stack)<{ $isReadonly: boolean }>`
  flex-direction: row;
  align-items: center;
  &[data-is-active='true'] {
    ${TreeCategoryArrowIcon} {
      display: none;
    }
  }
  &[data-is-active='false'] {
    ${MenuIconButton} {
      display: none;
    }
  }
  &:hover {
    ${TreeCategoryArrowIcon} {
      display: ${props => (props.$isReadonly ? 'inline-block' : 'none')};
    }
    ${MenuIconButton} {
      display: inline-flex;
    }
  }
`;

const TreeNodeContainer = styled(Stack)`
  flex-direction: row;
  min-width: 0;
`;

const TreeRowTitle = styled(Typography)<{
  $level: number;
  $isSelected: boolean;
  $isCategory: boolean;
}>`
  cursor: pointer;
  background-color: ${props =>
    props.$isSelected ? props.theme.custom.palette.primary200 : 'transparent'};
  padding: 0.2rem 0.5rem;
  border-radius: 0.5rem;
  font-size: ${props => getTreeNodeFontSize(props.$level)}rem;
  font-weight: ${props =>
    getTreeNodeFontWeight(props.$level, props.$isCategory)};
  color: ${props => getTreeNodeFontColor(props.$level, props.$isCategory)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 1;
`;

type DataType = CoreDocumentTypeItem;

type Props = {
  items: DataType[];
  isLoading?: boolean;
  isLoaded?: boolean;
  isReadonly?: boolean;
  selectedDocumentId?: string;
  sectionId: DocumentsSections;
  saveDocument?: (data: Partial<APIDocument>) => void;
  deleteDocument?: (documentId: string) => void;
  onSaveDocument?: (document: APIDocument) => void;
  onAddDocument?: (document: APIDocument) => void;
  onDeleteDocument?: () => void;
  onDocumentClick: (document: APIDocument) => void;
  treeProps?: Partial<TreeProps<DataType>>;
  hasSections?: boolean;
  editHiddenFields?: DocumentFields[];
};

export const DocsSectionsTree: React.FC<Props> = ({
  items,
  isLoading = false,
  isLoaded = false,
  isReadonly = false,
  selectedDocumentId,
  sectionId,
  saveDocument,
  deleteDocument,
  onSaveDocument,
  onAddDocument,
  onDeleteDocument,
  onDocumentClick,
  treeProps,
  hasSections = true,
  editHiddenFields,
}) => {
  const { t } = useTranslation();
  const treeRef = useRef<TreeApi<DataType>>(null);

  const {
    width: treeContainerWidth,
    height: treeContainerHeight,
    ref: treeResizeDetectorRef,
  } = useResizeDetector();

  const [addToTreeRootMenuAnchorEl, setAddToTreeRootMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [treeMenuAnchorEl, setTreeMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [isDocumentDeletePending, setIsDocumentDeletePending] = useState(false);
  const [treeMenuNode, setTreeMenuNode] = useState<NodeApi<DataType> | null>(
    null,
  );
  const [nodeToDelete, setNodeToDelete] = useState<NodeApi<DataType> | null>(
    null,
  );
  const [draftDocument, setDraftDocument] =
    useState<null | Partial<APIDocument>>(null);

  const onMove = useCallback<MoveHandler<DataType>>(
    async ({ dragIds, dragNodes, parentId, index }) => {
      const dragNode = dragNodes[0];
      if (dragNode) {
        await saveDocument?.({
          ...dragNode.data.document,
          parentId: parentId || '',
          position: index,
        });
        const nextChildren: NodeApi<DataType>[] = (
          dragNode.parent?.children || []
        )
          .filter((it: { id: string }) => it.id !== dragIds[0])
          .slice(index);
        nextChildren.forEach((node, internalIndex: number) => {
          saveDocument?.({
            ...node.data.document,
            position: index + internalIndex + 1,
          });
        });
      }
    },
    [saveDocument],
  );

  const onClick = useCallback<MouseEventHandler<HTMLElement>>(
    event => {
      const menuButton = (event.target as HTMLElement).closest(
        `[data-node-menu-button="true"]`,
      );
      const nodeRow = (event.target as HTMLElement).closest(
        `[data-node-row="true"]`,
      );
      if (nodeRow instanceof HTMLElement) {
        const node = nodeRow && treeRef.current!.get(nodeRow.dataset.nodeId!);
        if (node) {
          if (menuButton instanceof HTMLElement) {
            event.stopPropagation();
            nodeRow.dataset.isActive = 'true';
            setTreeMenuAnchorEl(menuButton);
            setTreeMenuNode(node);
          } else {
            onDocumentClick(node.data.document);
          }
        }
      }
    },
    [onDocumentClick],
  );

  const onItemMenuClose = useCallback(() => {
    if (treeMenuNode) {
      const nodeRow = document.getElementById(getNodeRowId(treeMenuNode.id));
      if (nodeRow) {
        nodeRow.dataset.isActive = 'false';
      }
    }
    setTreeMenuAnchorEl(null);
    setTreeMenuNode(null);
  }, [treeMenuNode]);

  const onMenuCreateSectionClicked = useCallback(() => {
    onItemMenuClose();
    treeMenuNode &&
      setDraftDocument({
        [DocumentFields.ParentId]: treeMenuNode.data.id,
        [DocumentFields.SectionId]: sectionId,
        [DocumentFields.IsCategory]: true,
        [DocumentFields.Position]: treeMenuNode.children?.length ?? 0,
      });
  }, [setDraftDocument, onItemMenuClose, treeMenuNode, sectionId]);

  const onMenuCreateArticleClicked = useCallback(() => {
    onItemMenuClose();
    treeMenuNode &&
      setDraftDocument({
        [DocumentFields.ParentId]: treeMenuNode.data.id,
        [DocumentFields.SectionId]: sectionId,
        [DocumentFields.Position]: treeMenuNode.children?.length ?? 0,
      });
  }, [onItemMenuClose, treeMenuNode, sectionId]);

  const onMenuRenameClicked = useCallback(() => {
    onItemMenuClose();
    treeMenuNode &&
      setDraftDocument({
        ...treeMenuNode.data.document,
      });
  }, [onItemMenuClose, treeMenuNode]);

  const onMenuDeleteClicked = useCallback(() => {
    onItemMenuClose();
    setNodeToDelete(treeMenuNode);
  }, [onItemMenuClose, treeMenuNode]);

  const onTreeRootAddClicked = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAddToTreeRootMenuAnchorEl(event.currentTarget);
    },
    [],
  );

  const onAddToTreeRootMenuClosed = useCallback(() => {
    setAddToTreeRootMenuAnchorEl(null);
  }, []);

  const onMenuCreateRootSectionClicked = useCallback(() => {
    onAddToTreeRootMenuClosed();
    setDraftDocument({
      [DocumentFields.SectionId]: sectionId,
      [DocumentFields.IsCategory]: true,
      [DocumentFields.Position]: items.length,
    });
  }, [items.length, onAddToTreeRootMenuClosed, sectionId]);

  const onMenuCreateRootArticleClicked = useCallback(() => {
    onAddToTreeRootMenuClosed();
    setDraftDocument({
      [DocumentFields.SectionId]: sectionId,
      [DocumentFields.Position]: items.length,
    });
  }, [onAddToTreeRootMenuClosed, items.length, sectionId]);

  const onConfirmDeleteDocument = useCallback(async () => {
    if (nodeToDelete) {
      setIsDocumentDeletePending(true);
      await deleteDocument?.(nodeToDelete.data.id);
      setIsDocumentDeletePending(false);
      setNodeToDelete(null);
      onDeleteDocument?.();
    }
  }, [onDeleteDocument, deleteDocument, nodeToDelete]);

  const disableDrop = useCallback(
    ({ parentNode }: { parentNode: NodeApi<DataType> }) => {
      return parentNode.isRoot ? false : !parentNode.data.isCategory;
    },
    [],
  );

  return (
    <Wrapper ref={treeResizeDetectorRef}>
      {isLoading || !isLoaded ? (
        <Skeleton
          rows={100}
          rowSpacing={25}
          width={400}
          height={20}
          loading={true}
        />
      ) : (
        <>
          {items.length ? (
            <TreeWrapper>
              <ReactArboristTree
                ref={treeRef}
                selection={selectedDocumentId}
                disableMultiSelection
                disableEdit={isReadonly}
                disableDrag={isReadonly}
                // @ts-ignore find out the right type
                disableDrop={disableDrop}
                isReadonly={isReadonly}
                data={items}
                width={treeContainerWidth}
                height={treeContainerHeight}
                // @ts-ignore find out the right type
                renderRow={TreeRow}
                renderCursor={TreeDragCursor}
                // @ts-ignore no need to fit the correct type with _.noop
                onCreate={_.noop}
                indent={12}
                onRename={_.noop}
                // @ts-ignore find out the right type
                onMove={onMove}
                onDelete={_.noop}
                onClick={onClick}
                {...treeProps}
              >
                {TreeNode}
              </ReactArboristTree>
            </TreeWrapper>
          ) : (
            <EmptyState>
              <EmptyStateText>{t('docs.noDocumentsText')}</EmptyStateText>
            </EmptyState>
          )}
          {!isReadonly && (
            <StyledAddButton
              label={t('docs.addNew')}
              onClick={onTreeRootAddClicked}
            />
          )}
          {draftDocument && (
            <DocsSectionsTreeModal
              open
              header={draftDocument.id ? t('common.edit') : t('common.new')}
              confirmLabel={
                draftDocument.id ? t('common.save') : t('common.add')
              }
              document={draftDocument}
              saveDocument={saveDocument}
              onClose={() => setDraftDocument(null)}
              onSuccess={draftDocument.id ? onSaveDocument : onAddDocument}
              hiddenFields={editHiddenFields}
            />
          )}
          <Menu
            id="add-to-tree-root-menu"
            anchorEl={addToTreeRootMenuAnchorEl}
            open={!!addToTreeRootMenuAnchorEl}
            onClose={onAddToTreeRootMenuClosed}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            {hasSections && (
              <MenuItem onClick={onMenuCreateRootSectionClicked}>
                <ListItemIcon>
                  <CreateNewFolderIcon />
                </ListItemIcon>
                <ListItemText>{t('docs.menu.addSection')}</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={onMenuCreateRootArticleClicked}>
              <ListItemIcon>
                <ArticleIcon />
              </ListItemIcon>
              <ListItemText>{t('docs.menu.addArticle')}</ListItemText>
            </MenuItem>
          </Menu>
          {treeMenuNode && (
            <Menu
              id="tree-menu"
              open
              anchorEl={treeMenuAnchorEl}
              onClose={onItemMenuClose}
            >
              {treeMenuNode.data.isCategory && hasSections && (
                <MenuItem onClick={onMenuCreateSectionClicked}>
                  <ListItemIcon>
                    <CreateNewFolderIcon />
                  </ListItemIcon>
                  <ListItemText>{t('docs.menu.addSection')}</ListItemText>
                </MenuItem>
              )}
              {treeMenuNode.data.isCategory && (
                <MenuItem onClick={onMenuCreateArticleClicked}>
                  <ListItemIcon>
                    <ArticleIcon />
                  </ListItemIcon>
                  <ListItemText>{t('docs.menu.addArticle')}</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={onMenuRenameClicked}>
                <ListItemIcon>
                  <EditIcon />
                </ListItemIcon>
                <ListItemText>{t('docs.menu.rename')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={onMenuDeleteClicked}>
                <ListItemIcon>
                  <DeleteIcon />
                </ListItemIcon>
                <ListItemText>{t('docs.menu.delete')}</ListItemText>
              </MenuItem>
            </Menu>
          )}
          <ConfirmationDialog
            onClose={() => setNodeToDelete(null)}
            open={!!nodeToDelete}
            actionPending={isDocumentDeletePending}
            handleActionClick={onConfirmDeleteDocument}
            description={t('docs.deleteConfirmation.text')}
          />
        </>
      )}
    </Wrapper>
  );
};

const TreeNode = ({ node, style, dragHandle }: NodeRendererProps<DataType>) => {
  return (
    <TreeNodeContainer
      style={style}
      ref={dragHandle}
      onClick={() => {
        node.data.isCategory && node.toggle();
      }}
    >
      <TreeRowTitle
        $level={node.level}
        $isSelected={node.isSelected}
        $isCategory={node.data.isCategory}
        title={node.data.name || ''}
      >
        {node.data.name}
      </TreeRowTitle>
    </TreeNodeContainer>
  );
};

const TreeRow = ({
  node,
  innerRef,
  attrs,
  children,
}: RowRendererProps<DataType>) => {
  return (
    <TreeRowWrapper
      ref={innerRef}
      {...attrs}
      id={getNodeRowId(node.id)}
      data-is-active={false}
      data-node-row={true}
      data-node-id={node.id}
      // @ts-ignore no possibilities to define types of additional TreeProp like `isReadonly`
      $isReadonly={node.tree.props.isReadonly}
    >
      <TreeNodeWrapper>{children}</TreeNodeWrapper>
      {node.data.isCategory && !!node.children?.length && (
        <TreeCategoryArrowIcon
          onClick={() => node.data.isCategory && node.toggle()}
        >
          {node.isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </TreeCategoryArrowIcon>
      )}
      {/* @ts-ignore no possibilities to define types of additional TreeProp like `isReadonly` */}
      {!node.tree.props.isReadonly && (
        <MenuIconButton
          data-node-menu-button={true}
          disableFocusRipple
          size="small"
        >
          <MoreVertIcon sx={{ fontSize: '1.5rem' }} />
        </MenuIconButton>
      )}
    </TreeRowWrapper>
  );
};

const TreeDragCursor = ({ top, left }: CursorProps) => {
  return <TreeCursor style={{ top, left }} />;
};
